import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CompleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import Layout from '../components/layout/Calayout';

const DeadlineAlert = () => {
  const [tasks, setTasks] = useState([
    {
      id: 1,
      title: 'Client Tax Return',
      deadline: new Date(2024, 4, 15),
      priority: 'high',
      status: 'pending',
      client: 'Sarah Johnson'
    },
    {
      id: 2,
      title: 'Financial Audit',
      deadline: new Date(2024, 4, 22),
      priority: 'medium',
      status: 'in-progress',
      client: 'Acme Corporation'
    }
  ]);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // Calculate days remaining and categorize tasks
  const categorizedTasks = useMemo(() => {
    const now = new Date();
    return {
      overdue: tasks.filter(task => 
        new Date(task.deadline) < now && task.status !== 'completed'
      ),
      urgent: tasks.filter(task => {
        const daysLeft = Math.ceil((new Date(task.deadline) - now) / (1000 * 60 * 60 * 24));
        return daysLeft <= 7 && daysLeft > 0 && task.status !== 'completed';
      }),
      upcoming: tasks.filter(task => {
        const daysLeft = Math.ceil((new Date(task.deadline) - now) / (1000 * 60 * 60 * 24));
        return daysLeft > 7 && task.status !== 'completed';
      }),
      completed: tasks.filter(task => task.status === 'completed')
    };
  }, [tasks]);

  const renderTaskCard = (task) => {
    const daysLeft = Math.ceil((new Date(task.deadline) - new Date()) / (1000 * 60 * 60 * 24));

    return (
      <Card 
        key={task.id} 
        sx={{ 
          marginBottom: 2,
          borderLeft: `5px solid ${
            task.priority === 'high' ? 'red' : 
            task.priority === 'medium' ? 'orange' : 'green'
          }`
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">{task.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                Client: {task.client}
              </Typography>
            </Box>
            <Button 
              startIcon={<EditIcon />}
              onClick={() => {
                setSelectedTask(task);
                setOpenEditDialog(true);
              }}
            >
              Edit
            </Button>
          </Box>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid item xs={6}>
              <Chip 
                label={`Deadline: ${task.deadline.toLocaleDateString()}`} 
                color="primary" 
                variant="outlined" 
              />
            </Grid>
            <Grid item xs={6}>
              <Chip 
                label={`${daysLeft} days left`} 
                color={daysLeft <= 3 ? 'error' : 'warning'} 
              />
            </Grid>
            <Grid item xs={12}>
              <Chip 
                label={task.status} 
                color={
                  task.status === 'pending' ? 'warning' : 
                  task.status === 'in-progress' ? 'info' : 
                  'success'
                } 
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderTaskSection = (title, tasks, icon) => (
    <Box sx={{ marginBottom: 3 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: 2 
        }}
      >
        {icon} {title} ({tasks.length})
      </Typography>
      {tasks.map(renderTaskCard)}
    </Box>
  );

  const handleUpdateTask = () => {
    setTasks(tasks.map(task => 
      task.id === selectedTask.id ? selectedTask : task
    ));
    setOpenEditDialog(false);
    setSelectedTask(null);
  };

  return (
    <Layout>
      <Box sx={{ padding: 3, backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
        <Typography variant="h4" color="primary" sx={{ marginBottom: 3 }}>
          Deadline Alerts
        </Typography>

        {renderTaskSection(
          'Overdue Tasks', 
          categorizedTasks.overdue, 
          <WarningIcon color="error" sx={{ marginRight: 1 }} />
        )}

        {renderTaskSection(
          'Urgent Tasks', 
          categorizedTasks.urgent, 
          <WarningIcon color="warning" sx={{ marginRight: 1 }} />
        )}

        {renderTaskSection(
          'Upcoming Tasks', 
          categorizedTasks.upcoming, 
          <CompleteIcon color="info" sx={{ marginRight: 1 }} />
        )}

        {renderTaskSection(
          'Completed Tasks', 
          categorizedTasks.completed, 
          <CompleteIcon color="success" sx={{ marginRight: 1 }} />
        )}

        {/* Edit Task Dialog */}
        {selectedTask && (
          <Dialog 
            open={openEditDialog} 
            onClose={() => setOpenEditDialog(false)}
          >
            <DialogTitle>Edit Task</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ marginTop: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Task Title"
                    value={selectedTask.title}
                    onChange={(e) => setSelectedTask(prev => ({
                      ...prev, 
                      title: e.target.value
                    }))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Deadline"
                    value={selectedTask.deadline.toISOString().split('T')[0]}
                    onChange={(e) => setSelectedTask(prev => ({
                      ...prev, 
                      deadline: new Date(e.target.value)
                    }))}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
              <Button 
                color="primary" 
                variant="contained"
                onClick={handleUpdateTask}
              >
                Update Task
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Layout>
  );
};

export default DeadlineAlert;